import React, { Fragment, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { lazyWithRetry, useLazyWithoutRetry } from "../core/lazy_with_retry";
import { usePartnerCatalogsQuery } from "./api";
import { useAnalytics } from "../lib/analytics/analytics";
import { useLocation } from "react-router";
import { PartnerCatalog } from "../data/catalog";
import { buildPathWithParams, useQueryString } from "../hooks";
import { CatalogBase } from "./template_catalog_base";
import { useDynamicPartnerConfigLocale } from "@/offering_v2/hooks/use_dynamic_partner_config_locale";
import { DiscountPopupProvider } from "@/core/discount_popup_provider";

const MindspaceCatalogBase = lazyWithRetry(
  () => import("../offering_v2/mindspace/template_catalog_base_mindspace")
);

interface CatalogPageParams {
  partnerSlug: string;
  catalogSlug?: string;
}

interface CatalogPageSearchParams {
  fromCatalog?: string;
  promoCode?: string;
}
export function CatalogPage() {
  const pageParams = useParams<CatalogPageParams>();
  const { fromCatalog, promoCode: queryPromoCode } =
    useQueryString<CatalogPageSearchParams>();
  const partnerSlug = pageParams.partnerSlug;
  const { catalogSlug } = pageParams;
  const history = useHistory();

  /**
   * All cases
   * - `partnerCatalogs.length === 1`
   *   + `partnerCatalogs[0].template === "original"`
   *   + `partnerCatalogs[0].template === "flexible"`
   *   + `partnerCatalogs[0].template === "mindspace"`
   * - `partnerCatalogs.length > 1`
   *   + Partner has multiple catalog pages intentionally
   */
  const catalogs = usePartnerCatalogsQuery(partnerSlug);
  let catalog: PartnerCatalog | undefined = catalogs[0];

  if (catalogs.length > 1) {
    catalog = catalogs.find((c) => {
      const { slug, partnerCatalogCategories: categories } = c;

      if (catalogSlug || fromCatalog) {
        return slug === (catalogSlug || fromCatalog);
      }

      return (
        !slug &&
        !categories.some((category) =>
          category.catalogOfferings?.some((co) => co.membership)
        )
      );
    });
  }

  const [partner, setPartner] = useState<any>();
  import(`./${partnerSlug}/${partnerSlug}_partner_config.tsx`).then(
    (partner) => {
      setPartner(partner);
    }
  );
  useDynamicPartnerConfigLocale(partnerSlug);

  const Theme = useLazyWithoutRetry(
    () => import(`./${partnerSlug}/${partnerSlug}_theme.tsx`)
  );
  const Analytics = useLazyWithoutRetry(
    () => import(`./${partnerSlug}/${partnerSlug}_analytics.tsx`)
  );

  useEffect(() => {
    if (partner && catalog && !queryPromoCode) {
      if (catalog.promoCode) {
        const currentUrl = window.location.pathname;
        const updatedUrl = buildPathWithParams(currentUrl, {
          promoCode: catalog.promoCode,
        });
        history.replace(updatedUrl);
      }
    }
  }, [catalog, fromCatalog, history, partner, queryPromoCode]);

  if (!partner || !catalog) {
    // Should throw error?
    return null;
  }

  const { template } = catalog;

  if (!template) {
    // Should throw error?
    return null;
  }

  const isMindSpace = partnerSlug === "mindspace" || template === "mindspace";

  return (
    <DiscountPopupProvider partnerId={catalog.partner.id}>
      <Theme />
      <Analytics />
      <CatalogPageTracker partnerCatalog={catalog} />
      {isMindSpace ? (
        <MindspaceCatalogBase
          partnerCatalog={catalog}
          partnerConfig={partner.partnerConfig}
        />
      ) : (
        <CatalogBase
          partnerCatalog={catalog}
          partnerConfig={partner.partnerConfig}
        />
      )}
    </DiscountPopupProvider>
  );
}

function CatalogPageTracker(props: { partnerCatalog?: PartnerCatalog }) {
  const analytics = useAnalytics();
  const { pathname: pathName } = useLocation();
  const template = props.partnerCatalog?.template;
  const slug = props.partnerCatalog?.slug;
  const { id, internalName, name } = props.partnerCatalog?.partner || {};

  useEffect(() => {
    analytics.screen(
      "Catalog Page View",
      {
        partnerName: name,
        partnerInternalName: internalName,
        partnerId: id,
        pageName: "Catalog",
        catalogSlug: slug,
        Template: template,
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics, pathName, id, internalName, name, template, slug]);

  return <Fragment />;
}
